// CF-FLIP-UX-FOLLOWUP-V1: pure helpers extracted from History.tsx for
// callable unit testing under root jest. No React imports — these run in
// the root test harness which doesn't load the frontend Vite config.

export interface HistoryEntryShape {
    entry_type: string;
    ref_id: string | null;
}

// A history row is verifiable IFF it represents a flip event whose
// flip_request_id was recorded in ref_id. Backend writes WAGER_DEBIT +
// PAYOUT_CREDIT pairs with the same flip_request_id (admin.ts:111;
// flip-engine.ts:119,290). Every other ledger entry type (deposits,
// withdrawals, refunds, reversals) is non-verifiable — no flip ID,
// nothing to surface to the Verify tab.
// CF-ACTIVITY-CHIP-FLIPS-V1: there are FOUR flip leg types, not two. Burn-first funding
// (flip-engine.ts:127-141) spends sticky chips before cash, so a single flip writes a
// CHIP_WAGER for the chip portion AND a WAGER_DEBIT when cashGrossNano > 0. A mixed win
// likewise writes CHIP_PAYOUT and PAYOUT_CREDIT under the same flip_request_id. Chip-funded
// flips are just as verifiable as cash ones — same flip_request_id, same provable RNG.
export const FLIP_WAGER_TYPES = ['WAGER_DEBIT', 'CHIP_WAGER'] as const;
export const FLIP_PAYOUT_TYPES = ['PAYOUT_CREDIT', 'CHIP_PAYOUT'] as const;
const FLIP_LEG_TYPES: readonly string[] = [...FLIP_WAGER_TYPES, ...FLIP_PAYOUT_TYPES];

export function isVerifiableHistoryEntry(entry: HistoryEntryShape): boolean {
    return FLIP_LEG_TYPES.includes(entry.entry_type) && entry.ref_id !== null;
}

// Visible short identifier for a flip_request_id (UUIDv4 in the backend).
// Used as the on-row affordance hint — the full ID still lives in title
// + aria-label so screen readers + tooltips read the verifiable value.
export function shortRefId(refId: string): string {
    return refId.slice(-8);
}

// ─────────────────────────────────────────────────────────────────────────
// CF-HISTORY-ONE-LINE-PER-FLIP — display grouping (PL #7058)
//
// A WINNING flip writes TWO ledger legs that share one flip_request_id:
// WAGER_DEBIT (the stake out) + PAYOUT_CREDIT (the winnings in). Rendered as
// two rows, the SAME reference appears twice and reads like a reused ref / bug
// (PL #7051/#7058). This groups the legs by FULL ref_id into ONE display row.
//
// Display-only: ref_id is NEVER mutated (the full flip_request_id stays the
// shared verify identity). A LOSS has only a WAGER_DEBIT → one row. A
// PAYOUT_CREDIT whose matching WAGER_DEBIT fell outside the fetched /history
// window (the limit cut between two same-second legs) renders HONESTLY as a
// payout-only row — never silently dropped.
//
// FAIL LOUD on anything that violates the backend's exactly-one-pairing
// invariant (admin.ts:111; flip-engine.ts:119,290): a second WAGER_DEBIT or a
// second PAYOUT_CREDIT for one ref_id returns { ok:false }. The caller surfaces
// the malformed-data screen instead of silently merging possibly-corrupt rows —
// a reused ref would be the exact fairness anomaly we must never hide.

export interface LedgerEntryShape {
    entry_type: string;
    amount_nano: string;
    ref_id: string | null;
    created_at: number;
}

// CF-ACTIVITY-CHIP-FLIPS-V1: a flip row now holds LEG ARRAYS, not one wager slot and one
// payout slot. A mixed cash+chip flip legitimately has TWO wager legs, which the old
// single-slot shape could not represent — it would have failed loud on perfectly valid
// data. `wagerTotalNano` / `payoutTotalNano` are the per-ref aggregates the UI displays,
// so amount rendering and profit both read ONE summed figure per flip rather than a
// representative leg that silently omits the other half.
export type HistoryRow<T extends LedgerEntryShape> =
    | { kind: 'single'; entry: T }
    | {
          kind: 'flip';
          refId: string;
          createdAt: number;
          wagerLegs: T[];
          payoutLegs: T[];
          wagerTotalNano: string;
          payoutTotalNano: string;
      };

export type GroupHistoryResult<T extends LedgerEntryShape> =
    | { ok: true; rows: HistoryRow<T>[] }
    | { ok: false; reason: string };

// A flip leg is a WAGER_DEBIT / PAYOUT_CREDIT that carries its flip_request_id.
// WAGER_DEBIT / PAYOUT_CREDIT with a null ref_id are NOT groupable flip legs
// (and are non-verifiable per isVerifiableHistoryEntry) — they stay single rows.
function isFlipLeg(entry: LedgerEntryShape): boolean {
    return FLIP_LEG_TYPES.includes(entry.entry_type) && entry.ref_id !== null;
}

function isWagerLeg(entry: LedgerEntryShape): boolean {
    return (FLIP_WAGER_TYPES as readonly string[]).includes(entry.entry_type);
}

function rowCreatedAt<T extends LedgerEntryShape>(row: HistoryRow<T>): number {
    return row.kind === 'single' ? row.entry.created_at : row.createdAt;
}

// Sum a leg set in nano. Returns null on a malformed amount so the caller can fail loud
// rather than render a coerced zero — a dropped stake would understate what the player
// actually risked, which is the same class of dishonesty this ticket exists to remove.
function sumNano(legs: readonly LedgerEntryShape[]): string | null {
    let total = 0n;
    for (const leg of legs) {
        if (!/^\d+$/.test(leg.amount_nano)) return null;
        total += BigInt(leg.amount_nano);
    }
    return total.toString();
}

export function groupHistoryRows<T extends LedgerEntryShape>(entries: T[]): GroupHistoryResult<T> {
    const rows: HistoryRow<T>[] = [];
    const flipIndex = new Map<string, number>(); // ref_id -> index of its row in `rows`

    // CF-ACTIVITY-CHIP-FLIPS-V1: legality is now per SOURCE, not per direction. One flip may
    // carry a cash wager AND a chip wager (burn-first partial funding) — that is legal and must
    // group into ONE row. What remains illegal is two legs of the SAME entry_type for one ref:
    // two WAGER_DEBIT or two CHIP_WAGER would still mean a reused flip_request_id, the exact
    // fairness anomaly we must never hide by silently merging.
    const seenTypes = new Map<string, Set<string>>(); // ref_id -> entry_types already taken
    for (const e of entries) {
        if (!isFlipLeg(e)) {
            rows.push({ kind: 'single', entry: e });
            continue;
        }
        const refId = e.ref_id as string;
        const taken = seenTypes.get(refId);
        if (taken?.has(e.entry_type)) {
            return {
                ok: false,
                reason: isWagerLeg(e) ? 'duplicate_wager_for_ref' : 'duplicate_payout_for_ref',
            };
        }
        if (taken) { taken.add(e.entry_type); } else { seenTypes.set(refId, new Set([e.entry_type])); }

        const idx = flipIndex.get(refId);
        if (idx === undefined) {
            flipIndex.set(refId, rows.length);
            rows.push({
                kind: 'flip',
                refId,
                createdAt: e.created_at,
                wagerLegs: isWagerLeg(e) ? [e] : [],
                payoutLegs: isWagerLeg(e) ? [] : [e],
                wagerTotalNano: '0',
                payoutTotalNano: '0',
            });
            continue;
        }
        const row = rows[idx];
        if (row.kind !== 'flip') return { ok: false, reason: 'group_anchor_corrupt' };
        if (isWagerLeg(e)) { row.wagerLegs.push(e); } else { row.payoutLegs.push(e); }
    }

    // Aggregate per ref AFTER all legs are collected — a total computed from a partially
    // filled row would understate a mixed flip, which is the defect this ticket is fixing.
    for (const row of rows) {
        if (row.kind !== 'flip') continue;
        const w = sumNano(row.wagerLegs);
        const p = sumNano(row.payoutLegs);
        if (w === null || p === null) return { ok: false, reason: 'malformed_leg_amount' };
        row.wagerTotalNano = w;
        row.payoutTotalNano = p;
    }

    // Explicit, deterministic primary ordering: newest first by created_at.
    // Array.prototype.sort is stable (ES2019+), so rows sharing a created_at
    // second keep the backend ledger sequence as a deterministic tiebreaker
    // rather than that arrival order being the SOLE ordering signal. The whole
    // transform is pure — identical input always yields identical output.
    rows.sort((a, b) => rowCreatedAt(b) - rowCreatedAt(a));

    return { ok: true, rows };
}
